import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  signal,
  WritableSignal,
  SimpleChanges
} from '@angular/core';
import {
  InteractiveDataAnalysisSignals,
  SignalField
} from '@models/pages/interactive-data-analysis/interactive-data-analysis-signals';
import {
  InteractiveDataAnalysisService
} from '@services/left-menu/page/interactive-data-analysis/interactive-data-analysis.service';
import {TranslateService} from '@ngx-translate/core';
import {AbstractControl, UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {SignalsNotificationService} from '@services/pages/interactive-data-analysis/signals-notification.service';
import {IntervalTypes} from '@constants/pages/interactive-data-analysis/interval-types';
import {AggregationType} from '@models/pages/interactive-data-analysis/aggregation-type';
import {NotificationService} from '@services/notification/notification.service';
import {InteractiveDataAnalysisConfig} from '@models/pages/interactive-data-analysis/interactive-data-analysis-config';
import {
  InteractiveDataAnalysisSignalChartProperties
} from '@models/pages/interactive-data-analysis/interactive-data-analysis-signal-chart-properties';
import {TableHeaderSortableService} from '@services/table-header-sortable/table-header-sortable.service';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {ChartsHelperService} from '@services/charts/helper/charts-helper.service';
import {ColorPaletteService} from '@services/color-palette/color-palette.service';
import {EnvironmentConfig} from '@environments/environment';
import {ChartSignalEntry} from '@models/pages/user-defined-page/chart-signal-entry';
import {
  InteractiveDataAnalysisProperty
} from '@models/pages/interactive-data-analysis/interactive-data-analysis-property';
import {AggregationTypesService} from '@services/left-menu/page/interactive-data-analysis/aggregation-types.service';
import {
  AggregatedSignalData,
  ParsedSignalData
} from '@models/pages/interactive-data-analysis/interactive-data-analysis-data';

@Component({
  selector: 'scitis-interactive-data-analysis',
  templateUrl: './interactive-data-analysis.component.html',
  styleUrls: ['./interactive-data-analysis.component.scss'],
  standalone: false
})
export class InteractiveDataAnalysisComponent implements OnInit, OnChanges {

  @Input()
  public isCollapsed: boolean = false;
  @Input()
  public useColorPalette: boolean = false;
  @Input()
  public exportStarted: string = '';
  @Input()
  public machineId: string = '';
  @Input()
  public organizationId: string = '';
  @Input()
  public name: string = '';
  @Input()
  public updatedInteractiveDataAnalysisConfig: InteractiveDataAnalysisConfig = new InteractiveDataAnalysisConfig();
  @Input()
  public widgetInformation: any;

  public chartId: string = '';
  public startTimeLabel: string = '';
  public endTimeLabel: string = '';
  public signals: SignalField[] = [];
  public stringSignals: SignalField[] = [];
  public possibleAggregationUnits: AggregationType[] = [];
  public intervalTypes: any[] = [];
  public originalStringData: any = [];
  public chartSettings: any;
  public form: UntypedFormGroup = new UntypedFormGroup({});
  public isLoadingSignals: WritableSignal<boolean> = signal(true);
  public exportFileName: string = '';
  public exportFileType: string = 'csv';
  public aggregatedData: AggregatedSignalData = {};
  public dateNow: number = new Date().getTime();
  public unusableSignals: string[] = [];

  private interactiveDataAnalysisConfig: InteractiveDataAnalysisConfig = new InteractiveDataAnalysisConfig();
  private signalsOrderedByConfigurationsType: SignalField[] = [];
  private originalData: ParsedSignalData = [];
  private signalTypes: Map<string, string> = new Map<string, string>();
  private aggregationUnits: Map<string, AggregationType[]> = new Map<string, AggregationType[]>();
  private selectedInterval: IntervalTypes | undefined;
  private selectedAggregation: AggregationType | undefined;
  private interval: number = 10;
  private startDate: number = 0;
  private endDate: number = 0;
  private ONE_WEEK_IN_MINUTES: number = 25 * 7 * 60;
  private startInterval: Date = new Date();
  private endInterval: Date = new Date();
  private customBaseUnit: string = '';
  private interactiveDataAnalysisDetailsInterval: string = EnvironmentConfig.PortalSettings.interactiveDataAnalysisDetailsInterval;
  private readonly CURRENT_PAGE: string = 'InteractiveDataAnalysisComponent';

  constructor(
      private dataAnalysisService: InteractiveDataAnalysisService,
      private aggregationTypesService: AggregationTypesService,
      private translateService: TranslateService,
      private signalsNotificationService: SignalsNotificationService,
      private notificationService: NotificationService,
      private tableHeaderSortableService: TableHeaderSortableService,
      private loadingIndicatorService: LoadingIndicatorService,
      private chartsHelperService: ChartsHelperService,
      private colorPaletteService: ColorPaletteService,
      private changeDetectorRef: ChangeDetectorRef
  ) {
    this.startTimeLabel = this.translateService.instant('Start');
    this.endTimeLabel = this.translateService.instant('End');
  }

  public get showCustomDatePicker(): boolean {
    return this.form.get('intervalType')?.value === IntervalTypes.CUSTOM;
  }

  public get formControl(): { [p: string]: AbstractControl } {
    return this.form.controls;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes.exportStarted && changes.exportStarted.currentValue) {
      this.triggerChartDataExportExport(changes.exportStarted.currentValue);
    }
    if (changes?.useColorPalette && !changes.useColorPalette.firstChange) {
      const selectedSignals: string[] = this.formControl?.signals?.value;
      this.updateChartSignal(selectedSignals || []);
      this.refreshChartData();
    }

    if (changes?.updatedInteractiveDataAnalysisConfig?.currentValue && this.formControl.signals) {
      const selectedSignals: string[] = this.formControl?.signals?.value;
      this.updateChartSignal(selectedSignals);
      this.refreshChartData();
    }
  }

  ngOnInit(): void {
    this.intervalTypes = this.dataAnalysisService.getIntervalTypes();
    this.aggregationUnits = this.dataAnalysisService.getPossibleUnits();
    if (!this.widgetInformation) {
      this.possibleAggregationUnits = this.dataAnalysisService.getPossibleAggregationUnits(IntervalTypes.CURRENT_DAY, this.aggregationUnits);
      this.generateForm(IntervalTypes.CURRENT_DAY, this.aggregationTypesService.TYPE_MINUTES15_WITH_DEFAULT);
      this.setIntervalType({id: IntervalTypes.CURRENT_DAY});
    } else {
      this.possibleAggregationUnits = this.dataAnalysisService.getPossibleAggregationUnits(this.interactiveDataAnalysisDetailsInterval, this.aggregationUnits);
      this.generateForm(this.interactiveDataAnalysisDetailsInterval, this.aggregationTypesService.TYPE_MINUTE);
      this.setIntervalType({id: this.interactiveDataAnalysisDetailsInterval});
    }

    this.chartId = 'interactive-analysis';

    this.chartSettings = {
      rangeType: 'TIME',
      rangeValue: this.ONE_WEEK_IN_MINUTES,
      signals: [],
    };
    this.listAllPossibleSignalsForMachine(this.machineId);
    if (this.widgetInformation) {
      const signalNames: string [] = this.dataAnalysisService.getSelectedSignalsFromUserDefinedPage(this.widgetInformation.selectedSignals);
      this.loadChartFromUDP(signalNames, this.widgetInformation.selectedStringSignals, this.widgetInformation.selectedSignals);
      this.formControl.signals.patchValue(signalNames);
    }
  }

  public onSelectAll(): void {
    const selected = this.signals.map(item => item.id);
    this.formControl.signals.patchValue(selected);
  }

  onClearAll() {
    this.formControl.signals.patchValue([]);
  }

  getStartTime() {
    return {hour: 0, minute: 0, second: 0};
  }

  public onFocusOutStartTime(event): void {
    if (!event || !event.date || !event.time || event.time.hour === undefined || event.time.minute === undefined) {
      return;
    }
    this.startDate = this.dataAnalysisService.getDate(event);
    this.calculateCustomTimeAggregation();
  }

  public onFocusOutEndTime(event): void {
    if (!event || !event.date || !event.time || event.time.hour === undefined || event.time.minute === undefined) {
      return;
    }
    this.endDate = this.dataAnalysisService.getDate(event);
    this.calculateCustomTimeAggregation();
  }

  public setIntervalType(event): void {
    if (!event || !event.id) {
      return;
    }
    this.resetData();
    this.selectedInterval = event.id;
    const intervals = this.dataAnalysisService.getIntervals(event.id);
    this.endInterval = intervals.endInterval;
    this.startInterval = intervals.startInterval;
    this.interval = intervals.interval;

    this.possibleAggregationUnits = this.dataAnalysisService.getPossibleAggregationUnits(event.id, this.aggregationUnits);
    this.customBaseUnit = this.dataAnalysisService.getDefaultDisplayInterval(event.id, this.aggregationUnits);
    this.formControl.aggregationType.setValue(this.customBaseUnit);
    this.updateChartIntervalProperties();
  }

  public setAggregationType(event): void {
    this.aggregatedData = {};
    this.selectedAggregation = event;
    this.customBaseUnit = event;
    this.updateChartIntervalProperties();
    if (this.originalData && this.originalData.length > 0) {
      setTimeout(() => {
        this.aggregatedData = {...this.dataAnalysisService.aggregateData(this.originalData, this.customBaseUnit)};
      }, 100);

    }
  }

  public update(): void {
    this.loadingIndicatorService.show();
    this.updateChartIntervalProperties();
    this.dataAnalysisService.generateIDADefaultSignalsVisibilitySettings(this.machineId, this.formControl.signals.value);

    const startInterval = this.selectedInterval === IntervalTypes.CUSTOM ? this.startDate : this.startInterval.getTime();
    const endInterval = this.selectedInterval === IntervalTypes.CUSTOM ? this.endDate : this.endInterval.getTime();


    const selectedSignals: string[] = this.formControl.signals.value;
    const selectedString = this.formControl.stringSignal.value;

    this.updateChartSignal(selectedSignals);
    this.getDataVisualizationValues(String(startInterval), String(endInterval), selectedSignals);
    this.getDataVisualizationStringValues(String(startInterval), String(endInterval), selectedString);
  }

  private loadChartFromUDP(signalNames: string[], selectedString, selectedSignals: ChartSignalEntry[]): void {
    this.loadingIndicatorService.show();
    this.updateChartIntervalProperties();

    this.updateChartSignalsFromUDP(selectedSignals);
    this.getDataVisualizationValues(String(this.startInterval.getTime()), String(this.endInterval.getTime()), signalNames);
    this.getDataVisualizationStringValues(String(this.startInterval.getTime()), String(this.endInterval.getTime()), selectedString);
  }

  public onSignalSelect(): void {
    const selectedSignals: string[] = this.formControl.signals.value;
    this.updateChartSignal(selectedSignals);
    this.resetData();
  }

  public onStringSignalChange(): void {
    this.resetData();
  }

  private generateForm(intervalType: string, aggregationType: AggregationType): void {
    this.form = new UntypedFormGroup({
      signals: new UntypedFormControl(''),
      stringSignal: new UntypedFormControl('-1'),
      intervalType: new UntypedFormControl(intervalType),
      aggregationType: new UntypedFormControl(aggregationType.name)
    });

    this.setAggregationType(aggregationType);
  }

  private listAllPossibleSignalsForMachine(machineId: string): void {
    if (!machineId) {
      return;
    }
    this.dataAnalysisService.listAllPossibleSignalsForMachine(machineId).subscribe({
      next: (signals: InteractiveDataAnalysisSignals) => {
        if (signals.signals) {
          this.signals = this.dataAnalysisService.sortSignalList(signals.signals);
          this.signalsOrderedByConfigurationsType = this.tableHeaderSortableService.sortItems<SignalField>(signals.signals, 'machineSignal ', 'desc');
          this.signalsNotificationService.broadcast(this.signalsOrderedByConfigurationsType);
          this.setInteractiveDataAnalysisConfig(this.signals);
        }
        if (signals.stringSignals) {
          this.stringSignals = this.dataAnalysisService.sortSignalList(signals.stringSignals);
        }
        if (this.stringSignals?.[0]?.name !== this.dataAnalysisService.getNoStringSignal()?.name) {
          this.stringSignals.unshift(this.dataAnalysisService.getNoStringSignal());
        }
        if (signals.signalTypes) {
          this.signalTypes = signals.signalTypes;
        }
        this.isLoadingSignals.set(false);
      }, error: (error) => {
        this.notificationService.error(this.translateService.instant('Error retrieving all signals.'), error, this.CURRENT_PAGE, true);
        this.isLoadingSignals.set(false);
      }
    });
  }

  private calculateCustomTimeAggregation(): void {
    if (!this.startDate || !this.endDate) {
      console.log('Start or end date not defined');
      return;
    }
    const units = this.dataAnalysisService.calculateCustomTimeAggregation(this.endDate, this.startDate, this.aggregationUnits);
    this.customBaseUnit = units.customBaseUnit;
    this.interval = units.interval;
    this.possibleAggregationUnits = units.aggregationUnits;
    this.formControl.aggregationType.setValue(this.customBaseUnit);
  }

  private updateChartIntervalProperties(): void {
    if (!this.chartSettings) {
      this.chartSettings = {};
    }
    this.chartSettings.intervalDetails = {
      selectedInterval: this.selectedInterval,
      settings: this.selectedAggregation
    };
  }

  private getDataVisualizationValues(startInterval: string, endInterval: string, selectedSignals: string[]): void {
    this.resetData();

    const requestSignalList: string[] = this.dataAnalysisService.getRequestSignals(selectedSignals, this.signalTypes);
    const dataRequest = this.dataAnalysisService.getDataRequest(requestSignalList, startInterval, endInterval, String(this.interval), Number(this.machineId), Number(this.organizationId));

    this.dataAnalysisService.getDataVisualizationValues(Number(this.machineId), Number(this.organizationId), dataRequest).subscribe({
      next: (result) => {
        if (result && result.values) {
          this.originalData = this.dataAnalysisService.getParsedData(result.values);
          this.aggregatedData = {...this.dataAnalysisService.aggregateData(this.originalData, this.customBaseUnit)};
          this.loadingIndicatorService.hide();
        }
        this.unusableSignals = [...result.unusableSignals];
      }, error: (error) => {
        this.handleErrorResult(error);
        this.loadingIndicatorService.hide();
      }
    });
  }

  private getDataVisualizationStringValues(startInterval: string, endInterval: string, selectedString: string): void {
    const dataStringRequest = this.dataAnalysisService.getStringDataRequest(selectedString, this.signalTypes, startInterval, endInterval, String(this.interval), Number(this.machineId), Number(this.organizationId));

    this.dataAnalysisService.getDataVisualizationStringValues(Number(this.machineId), Number(this.organizationId), dataStringRequest).subscribe({
      next: (result) => {
        const selectedStringSignal = this.dataAnalysisService.getStringSignal(selectedString, this.stringSignals);
        this.originalStringData = this.dataAnalysisService.getStringData(result, selectedStringSignal);
      }, error: (error) => {
        this.handleErrorResult(error);
      }
    });
  }

  private handleErrorResult(error): void {
    if (error.status === 911) {
      this.notificationService.error(this.translateService.instant('No data could be retrieved for specified interval.'), error, this.CURRENT_PAGE, true);
    } else {
      this.notificationService.error(this.translateService.instant('Error retrieving chart data.'), error, this.CURRENT_PAGE, true);
    }
  }

  private setInteractiveDataAnalysisConfig(signals: SignalField[]): void {
    if (this.machineId) {
      this.dataAnalysisService.getConfiguration(this.machineId).subscribe({
        next: (interactiveDataAnalysisConfig: InteractiveDataAnalysisConfig) => {
          const {label, parentTab, properties} = interactiveDataAnalysisConfig;
          this.interactiveDataAnalysisConfig.chartId = this.chartId;
          this.interactiveDataAnalysisConfig.label = label;
          this.interactiveDataAnalysisConfig.parentTab = parentTab;
          this.interactiveDataAnalysisConfig.properties = this.parsePropertiesMap(properties, signals);

          this.signalsNotificationService.broadcastInteractiveDataAnalysisConfigNotification(this.interactiveDataAnalysisConfig);
        }, error: error => {
          console.log('Error getting configuration of interactive data analysis', error);
        }
      });
    }
  }

  private parsePropertiesMap(properties: any, signals: SignalField[]): Map<string, InteractiveDataAnalysisProperty> {
    const result: Map<string, InteractiveDataAnalysisProperty> = new Map<string, InteractiveDataAnalysisProperty>();
    if (!properties) {
      properties = new Map<string, InteractiveDataAnalysisProperty>();
    }
    for (const {id, name, color} of signals) {

      const elementById = properties[id];
      if (elementById) {
        result.set(id, elementById);
      } else {
        const elementByName = properties[name];
        if (elementByName) {
          result.set(id, elementByName);
        } else {
          result.set(id, new InteractiveDataAnalysisProperty(color));
        }
      }


    }
    return result;

  }


  private updateChartSignalsFromUDP(signals: ChartSignalEntry[]): void {
    this.chartSettings.signals = [];
    for (const signal of signals) {
      const updatedSignal: InteractiveDataAnalysisSignalChartProperties = this.updateChartSignalFromUDP(signal);
      this.chartSettings.signals.push(updatedSignal);
    }
  }

  private updateChartSignalFromUDP(signal: ChartSignalEntry): InteractiveDataAnalysisSignalChartProperties {
    const interactiveDataAnalysisSignalChartProperties: InteractiveDataAnalysisSignalChartProperties = new InteractiveDataAnalysisSignalChartProperties();

    const customRange = !(signal.maxY === 0 && signal.minY === 0);
    interactiveDataAnalysisSignalChartProperties.customRange = signal.customRange;
    interactiveDataAnalysisSignalChartProperties.minY = signal.minY;
    interactiveDataAnalysisSignalChartProperties.maxY = signal.maxY;
    interactiveDataAnalysisSignalChartProperties.axis = signal.axis;
    interactiveDataAnalysisSignalChartProperties.colors.main = this.getColorString(signal.colors.main);
    interactiveDataAnalysisSignalChartProperties.signalId = signal.signalName;
    interactiveDataAnalysisSignalChartProperties.signalName = signal.signalName;
    interactiveDataAnalysisSignalChartProperties.signalType = signal.signalType;
    interactiveDataAnalysisSignalChartProperties.customYAxisInterval = customRange;
    interactiveDataAnalysisSignalChartProperties.axisYLabel = signal.displayedText;
    interactiveDataAnalysisSignalChartProperties.displayedText = signal.displayedText;
    interactiveDataAnalysisSignalChartProperties.scale = signal.scale;
    interactiveDataAnalysisSignalChartProperties.unit = signal.unit;
    return interactiveDataAnalysisSignalChartProperties;
  }

  private getColorString(color: number): string {
    return this.chartsHelperService.createLineHex(color);
  }

  private updateChartSignal(signalNames: string[]): void {
    const newArray: InteractiveDataAnalysisSignalChartProperties[] = [];
    for (const signalName of signalNames) {
      const machineDefinedSignal = this.findSignalByName(signalName);

      if (!machineDefinedSignal) {
        continue;
      }

      const interactiveDataAnalysisSignalChartProperties: InteractiveDataAnalysisSignalChartProperties = this.dataAnalysisService.getFullDetailsOfSignal(machineDefinedSignal, this.signalsOrderedByConfigurationsType, this.interactiveDataAnalysisConfig);
      const customYAxisInterval = interactiveDataAnalysisSignalChartProperties.customRange;
      const signalFromLiveChart = this.widgetInformation?.selectedSignals?.find((signalInWidgetInformation) => signalInWidgetInformation.signalName === signalName);
      if (signalFromLiveChart) {
        interactiveDataAnalysisSignalChartProperties.colors.main = this.getColorString(signalFromLiveChart.colors.main);
      } else if ((!interactiveDataAnalysisSignalChartProperties.colors || !interactiveDataAnalysisSignalChartProperties.colors.main)) {
        interactiveDataAnalysisSignalChartProperties.colors.main
            = this.updatedInteractiveDataAnalysisConfig.properties.get(signalName)?.color ||
            this.interactiveDataAnalysisConfig.properties.get(signalName)?.color || machineDefinedSignal?.color || '#ccc';
      }
      if (this.useColorPalette) {
        interactiveDataAnalysisSignalChartProperties.colors.main = this.colorPaletteService.getNextColor();
      }

      interactiveDataAnalysisSignalChartProperties.signalId = machineDefinedSignal.table + '@' + machineDefinedSignal.name;
      interactiveDataAnalysisSignalChartProperties.signalName = machineDefinedSignal.table + '@' + machineDefinedSignal.name;
      interactiveDataAnalysisSignalChartProperties.signalType = machineDefinedSignal.type;
      interactiveDataAnalysisSignalChartProperties.customRange = true;
      interactiveDataAnalysisSignalChartProperties.customYAxisInterval = customYAxisInterval;

      if (!machineDefinedSignal.machineSignal) {
        interactiveDataAnalysisSignalChartProperties.axisYLabel = machineDefinedSignal.table + '.' + machineDefinedSignal.name;
        interactiveDataAnalysisSignalChartProperties.displayedText = machineDefinedSignal.table + '.' + machineDefinedSignal.name;
        interactiveDataAnalysisSignalChartProperties.scale = 1;
        interactiveDataAnalysisSignalChartProperties.unit = '';
      } else {
        interactiveDataAnalysisSignalChartProperties.axisYLabel = machineDefinedSignal.displayedText;
        interactiveDataAnalysisSignalChartProperties.displayedText = machineDefinedSignal.displayedText;
        interactiveDataAnalysisSignalChartProperties.scale = machineDefinedSignal.scale;
        interactiveDataAnalysisSignalChartProperties.unit = machineDefinedSignal.unit;
        interactiveDataAnalysisSignalChartProperties.customYAxisInterval = customYAxisInterval;
      }
      newArray.push(interactiveDataAnalysisSignalChartProperties);
    }
    this.chartSettings.signals = newArray;
  }

  private findSignalByName(signalId): SignalField | undefined {
    const filteredSignals = this.signalsOrderedByConfigurationsType.filter(m => m.id === signalId);
    return filteredSignals.length > 0 ? filteredSignals[0] : undefined;
  }

  private triggerChartDataExportExport(exportType: string): void {
    if (!exportType || exportType.isNullOrWhitespace()) {
      return;

    }
    this.exportFileName = this.generateExportFileName();
    this.exportFileType = exportType;
  }

  private generateExportFileName(): string {
    const name = 'export_';
    if (!this.selectedInterval) {
      this.selectedInterval = IntervalTypes.CURRENT_DAY;
    }
    const interval = this.selectedInterval;
    const selectedAggregationUnits = this.aggregationUnits.get(this.selectedInterval);
    let grouping = '';
    if (selectedAggregationUnits) {
      for (const aggregationUnit of selectedAggregationUnits) {
        if (aggregationUnit.name === this.customBaseUnit) {
          grouping = aggregationUnit.name;
          break;
        }
      }
    }
    const date = this.chartsHelperService.formatDate(new Date(), 'yyyyMMdd_HHmmss', false);

    return name + interval + '_' + grouping + '_' + date;
  }

  private resetData(): void {
    this.aggregatedData = {};
    this.originalData = [];
  }

  private refreshChartData() {
    const data = {...this.aggregatedData};
    this.aggregatedData = {};
    this.changeDetectorRef.detectChanges();
    this.aggregatedData = data;
  }
}
